import { useEffect, useState } from "react";
import { Header } from "@/components/ui/Header";
import { Footer } from "@/components/ui/Footer";
import { HomePage } from "@/home/page";
import { AboutPage } from "@/about/page";
import { ServicePage } from "@/service/page";
import { ArticlesPage } from "@/articles/page";
import { ArticlesByCategoryPage } from "@/articles/[category]/page";
import { ArticleDetailPage } from "@/articles/[category]/[id]/page";
import { MBSCPage } from "@/service/mbsc/page";
import { MbscDocsPage } from "@/service/mbsc/docs/page";
import { BusinessReportAiPage } from "@/service/gyoai/page";
import { EcPage } from "@/service/ec/page";
import { EcTermsPage } from "@/service/ec/terms/page";
import { EcPrivacyPolicyPage } from "@/service/ec/privacypolicy/page";
import { NotFoundPage } from "@/404/page";
import "./index.css";

function getPath() {
  const p = typeof window !== "undefined" ? window.location.pathname : "/";
  return p.endsWith("/") && p !== "/" ? p.slice(0, -1) : p;
}

export function App() {
  const [path, setPath] = useState(getPath);

  useEffect(() => {
    const handlePop = () => setPath(getPath());
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  // Send GA4 page_view on route change (SPA).
  useEffect(() => {
    const id = typeof window !== "undefined" ? window.__GA_MEASUREMENT_ID__ : undefined;
    if (id && typeof window.gtag === "function") {
      window.gtag("config", id, { page_path: path || "/" });
    }
  }, [path]);

  const navigate = (next: string) => {
    window.history.pushState({}, "", next);
    setPath(getPath());
  };

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <Header currentPath={path} onNavigate={navigate} />
      <main className="flex-1 pt-16">
        {path === "/" && <HomePage onNavigate={navigate} />}
        {path === "/about" && <AboutPage onNavigate={navigate} />}
        {path === "/service" && <ServicePage />}
        {path === "/service/mbsc" && <MBSCPage onNavigate={navigate} />}
        {path === "/service/gyoai" && <BusinessReportAiPage onNavigate={navigate} />}
        {path === "/service/ec" && <EcPage onNavigate={navigate} />}
        {path === "/service/ec/terms" && <EcTermsPage />}
        {path === "/service/ec/privacypolicy" && <EcPrivacyPolicyPage />}
        {(path === "/service/mbsc/docs" || path.startsWith("/service/mbsc/docs/")) && (() => {
          const slug =
            path === "/service/mbsc/docs"
              ? null
              : path.slice("/service/mbsc/docs/".length).replace(/\/$/, "");
          return <MbscDocsPage slug={slug || null} onNavigate={navigate} />;
        })()}
        {path.startsWith("/service/") &&
          path !== "/service" &&
          path !== "/service/mbsc" &&
          path !== "/service/gyoai" &&
          path !== "/service/ec" &&
          path !== "/service/ec/terms" &&
          path !== "/service/ec/privacypolicy" &&
          path !== "/service/mbsc/docs" &&
          !path.startsWith("/service/mbsc/docs/") && <NotFoundPage onNavigate={navigate} />}
        {path === "/articles" && <ArticlesPage onNavigate={navigate} />}
        {path.startsWith("/articles/") && (() => {
          const rest = path.slice("/articles/".length).replace(/\/$/, "").split("/").filter(Boolean);
          if (rest.length === 0) return <ArticlesPage onNavigate={navigate} />;
          if (rest.length === 1)
            return <ArticlesByCategoryPage categorySlug={rest[0]!} onNavigate={navigate} />;
          return (
            <ArticleDetailPage
              slug={rest[1]!}
              category={rest[0]!}
              onNavigate={navigate}
            />
          );
        })()}
        {path !== "/" &&
          path !== "/about" &&
          path !== "/service" &&
          !path.startsWith("/service/") &&
          !path.startsWith("/articles") && (
            <NotFoundPage onNavigate={navigate} />
          )}
      </main>
      <Footer onNavigate={navigate} />
    </div>
  );
}

export default App;
